import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Paper,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Divider,
  Alert,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';
import HtmlContentField from '../components/HtmlContentField';
import axios from 'axios';

interface FormField {
  id: string;
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  editable?: boolean; // Whether this field can be edited after submission
  order: number;
  options?: string[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    // Custom validation rules
    custom?: {
      enabled: boolean;
      rules: {
        id: string;
        type: 'compare_dates' | 'compare_numbers' | 'min_length' | 'max_length' | 'custom_pattern' | 'dependent_value';
        compareField?: string; // Field ID to compare with
        compareOperator?: 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal' | 'equal' | 'not_equal';
        value?: string | number; // Static value for comparison
        message: string; // Custom error message
        dependentValue?: string; // Value that triggers this validation
      }[];
    };
  };
  conditional?: {
    dependsOn: string; // ID of the field this depends on
    showWhen: string[]; // Values that will show this field
    hideWhen?: string[]; // Values that will hide this field
  };
  visibility?: {
    level?: 'public' | 'admin-only' | 'hidden'; // Who can see this field
    publicCanView?: boolean; // Whether public users can view (but not edit) this field
    description?: string; // Why this field has restricted visibility
  };
  layout?: {
    width: number; // 12 = full width, 6 = half width, 4 = third width, 3 = quarter width
    breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  };
  styling?: {
    textAlign?: 'left' | 'center' | 'right';
    fontSize?: string;
    fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
    color?: string;
    backgroundColor?: string;
    padding?: string;
    marginTop?: string;
    marginBottom?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  };
  pricing?: {
    enabled: boolean;
    options: { [optionValue: string]: number }; // Map option values to prices
  };
  htmlContent?: {
    content: string; // Raw HTML content with variables
    enableVariables: boolean; // Whether to process variables
    systemVariables?: string[]; // Available system variables
    fieldVariables?: string[]; // Available form field variables
  };
  displayInAdminTable?: boolean; // Whether this field should be displayed as a column in admin submissions table
}

interface FormData {
  title: string;
  description: string;
  fields: FormField[];
  allowEditing?: boolean; // Whether users can edit submissions for this form
  settings: {
    requirePayment: boolean;
    amount?: number;
    redirectUrl?: string;
    allowMultipleSubmissions: boolean;
    notifications?: {
      enableEmail: boolean;
      enableSMS: boolean;
      emailField?: string; // Field name that contains the email
      phoneField?: string; // Field name that contains the phone number
      emailTemplate?: {
        subject: string;
        body: string;
      };
      smsTemplate?: {
        message: string;
      };
    };
    pdfSettings?: {
      enablePublicPdf?: boolean; // Whether to allow public PDF access
      customTitle?: string; // Custom title for PDF
      customHeader?: string; // Custom header text
      customFooter?: string; // Custom footer text
      includeLogo?: boolean; // Whether to include organization logo
      logoUrl?: string; // URL to logo image
      showPaymentDetails?: boolean; // Whether to show payment details in public PDF
      customStyling?: {
        primaryColor?: string; // Primary color for PDF
        fontFamily?: string; // Font family for PDF
        fontSize?: string; // Base font size
        backgroundColor?: string; // Background color
      };
      fieldsLayout?: {
        showFieldLabels?: boolean; // Whether to show field labels
        groupSimilarFields?: boolean; // Whether to group similar fields together
        hideEmptyFields?: boolean; // Whether to hide fields with no data
        showFormData?: boolean; // Whether to show the Form Data section
        showSubmissionInfo?: boolean; // Whether to show the Submission Information section
      };
      htmlContent?: {
        enabled?: boolean; // Whether to include HTML content in PDF
        content?: string; // Raw HTML content with variables
        enableVariables?: boolean; // Whether to process variables
        position?: 'header' | 'footer' | 'before-fields' | 'after-fields'; // Where to place HTML content
      };
    };
  };
}

const fieldTypes = [
  { value: 'text', label: 'Text Input' },
  { value: 'email', label: 'Email' },
  { value: 'number', label: 'Number' },
  { value: 'tel', label: 'Phone' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Button' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'file', label: 'File Upload' },
  { value: 'heading', label: 'Heading/Title' },
  { value: 'paragraph', label: 'Paragraph Text' },
  { value: 'divider', label: 'Divider/Separator' },
  { value: 'html', label: 'Custom HTML Content' }
];

// Helper function to safely update notifications
const updateNotifications = (
  currentNotifications: FormData['settings']['notifications'],
  updates: Partial<FormData['settings']['notifications']>
): FormData['settings']['notifications'] => {
  const baseNotifications = {
    enableEmail: false,
    enableSMS: false,
    emailTemplate: {
      subject: 'Form Submission Confirmation',
      body: 'Thank you for your submission. We have received your form and will process it accordingly.'
    },
    smsTemplate: {
      message: 'Thank you for your submission. We have received your form and will contact you soon.'
    },
    ...currentNotifications,
    ...updates
  };
  return baseNotifications;
};

const FormBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const isEdit = id && id !== 'new';

  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    fields: [],
    allowEditing: true, // Default to allowing edits
    settings: {
      requirePayment: false,
      allowMultipleSubmissions: true,
      notifications: {
        enableEmail: false,
        enableSMS: false,
        emailTemplate: {
          subject: 'Form Submission Confirmation',
          body: 'Thank you for your submission. We have received your form and will process it accordingly.'
        },
        smsTemplate: {
          message: 'Thank you for your submission. We have received your form and will contact you soon.'
        }
      },
      pdfSettings: {
        enablePublicPdf: true,
        customTitle: '',
        customHeader: '',
        customFooter: 'BPDAA Management System',
        includeLogo: false,
        logoUrl: '',
        showPaymentDetails: true,
        customStyling: {
          primaryColor: '#007bff',
          fontFamily: 'Arial, sans-serif',
          fontSize: '12px',
          backgroundColor: '#ffffff'
        },
        fieldsLayout: {
          showFieldLabels: true,
          groupSimilarFields: false,
          hideEmptyFields: true,
          showFormData: true,
          showSubmissionInfo: true
        },
        htmlContent: {
          enabled: false,
          content: '',
          enableVariables: false,
          position: 'before-fields'
        }
      }
    }
  });

  const [fieldDialog, setFieldDialog] = useState<{
    open: boolean;
    field: FormField | null;
    isEdit: boolean;
  }>({
    open: false,
    field: null,
    isEdit: false
  });

  const [loading, setLoading] = useState(false);

  const fetchForm = useCallback(async () => {
    if (!isEdit || id === 'new') return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Ensure fields have required properties for compatibility
      const processedFormData = {
        ...response.data,
        fields: response.data.fields.map((field: any, index: number) => ({
          ...field,
          name: field.name || field.id || `field_${Date.now()}_${index}`,
          order: field.order !== undefined ? field.order : index
        }))
      };
      
      setFormData(processedFormData);
    } catch (error) {
      console.error('Error fetching form:', error);
      showSnackbar('Failed to fetch form', 'error');
      navigate('/admin/forms');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit, showSnackbar, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchForm();
    }
  }, [isEdit, fetchForm]);

  const handleSaveForm = async () => {
    if (!formData.title.trim()) {
      showSnackbar('Form title is required', 'error');
      return;
    }

    // Validate payment settings - allow zero base price but require it to be defined
    if (formData.settings.requirePayment && (formData.settings.amount === undefined || formData.settings.amount < 0)) {
      showSnackbar('Payment amount must be 0 or greater when payment is enabled', 'error');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Prepare form data with properly formatted settings
      const submissionData = {
        ...formData,
        settings: {
          ...formData.settings,
          // Ensure amount is included when payment is required
          ...(formData.settings.requirePayment && {
            amount: formData.settings.amount || 0
          })
        }
      };
      
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, submissionData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('Form updated successfully', 'success');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/forms`, submissionData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSnackbar('Form created successfully', 'success');
        navigate('/admin/forms');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      showSnackbar('Failed to save form', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddField = () => {
    const fieldId = Date.now().toString();
    const newField: FormField = {
      id: fieldId,
      name: `field_${fieldId}`,
      type: 'text',
      label: '',
      required: false,
      editable: true, // Default to editable
      order: formData.fields.length,
      visibility: {
        level: 'public',
        publicCanView: true,
        description: ''
      },
      layout: {
        width: 12, // Full width by default
        breakpoint: 'sm'
      },
      styling: {
        textAlign: 'left',
        fontWeight: 'normal',
        color: '#000000',
        backgroundColor: '#ffffff'
      },
      htmlContent: {
        content: '',
        enableVariables: false,
        systemVariables: [],
        fieldVariables: []
      },
      displayInAdminTable: false // Default to not showing in admin table
    };
    
    setFieldDialog({
      open: true,
      field: newField,
      isEdit: false
    });
  };

  const handleEditField = (field: FormField) => {
    setFieldDialog({
      open: true,
      field: { ...field },
      isEdit: true
    });
  };

  const handleSaveField = (field: FormField) => {
    if (!field.label.trim()) {
      showSnackbar('Field label is required', 'error');
      return;
    }

    // Ensure field has a name (generate from label if empty)
    const processedField = {
      ...field,
      name: field.name || field.label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || `field_${field.id}`
    };

    if (fieldDialog.isEdit) {
      setFormData(prev => ({
        ...prev,
        fields: prev.fields.map(f => f.id === field.id ? processedField : f)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        fields: [...prev.fields, processedField]
      }));
    }

    setFieldDialog({ open: false, field: null, isEdit: false });
  };

  const handleDeleteField = (fieldId: string) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const moveFieldUp = (fieldId: string) => {
    setFormData(prev => {
      const fields = [...prev.fields];
      const currentIndex = fields.findIndex(f => f.id === fieldId);
      
      if (currentIndex > 0) {
        // Swap the field with the one above it
        [fields[currentIndex], fields[currentIndex - 1]] = [fields[currentIndex - 1], fields[currentIndex]];
        
        // Update order values
        fields.forEach((field, index) => {
          field.order = index;
        });
      }
      
      return { ...prev, fields };
    });
  };

  const moveFieldDown = (fieldId: string) => {
    setFormData(prev => {
      const fields = [...prev.fields];
      const currentIndex = fields.findIndex(f => f.id === fieldId);
      
      if (currentIndex < fields.length - 1) {
        // Swap the field with the one below it
        [fields[currentIndex], fields[currentIndex + 1]] = [fields[currentIndex + 1], fields[currentIndex]];
        
        // Update order values
        fields.forEach((field, index) => {
          field.order = index;
        });
      }
      
      return { ...prev, fields };
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {isEdit ? 'Edit Form' : 'Create New Form'}
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => navigate(`/admin/forms/${id}/view`)}
            disabled={!isEdit}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveForm}
            disabled={loading}
          >
            {isEdit ? 'Update Form' : 'Save Form'}
          </Button>
        </Box>
      </Box>

      <Box display="flex" gap={3}>
        {/* Form Settings */}
        <Box flex={1}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Form Settings
            </Typography>
            
            <TextField
              fullWidth
              label="Form Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              margin="normal"
              required
            />
            
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              margin="normal"
              multiline
              rows={3}
            />

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.requirePayment}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, requirePayment: e.target.checked }
                  }))}
                />
              }
              label="Require Payment"
            />

            {formData.settings.requirePayment && (
              <TextField
                label="Amount (৳)"
                type="number"
                value={formData.settings.amount === 0 ? '0' : (formData.settings.amount || '')}
                onChange={(e) => {
                  const value = e.target.value;
                  let amount = 0;
                  if (value !== '') {
                    amount = parseFloat(value);
                    if (isNaN(amount)) amount = 0;
                  }
                  setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, amount }
                  }));
                }}
                margin="normal"
                fullWidth
                required
                helperText="Enter the payment amount required for form submission"
                inputProps={{ min: 0, step: 1 }}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.allowMultipleSubmissions}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, allowMultipleSubmissions: e.target.checked }
                  }))}
                />
              }
              label="Allow Multiple Submissions"
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom>
              Edit Control Settings
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowEditing !== false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    allowEditing: e.target.checked
                  }))}
                />
              }
              label="Allow Editing Submissions"
              sx={{ mb: 1 }}
            />

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
              When enabled, users can edit their submissions using their edit code. 
              Individual fields can also be marked as non-editable.
            </Typography>

            <TextField
              fullWidth
              label="Redirect URL (optional)"
              value={formData.settings.redirectUrl || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                settings: { ...prev.settings, redirectUrl: e.target.value }
              }))}
              margin="normal"
              placeholder="https://example.com/thank-you"
            />

            <Divider sx={{ my: 3 }} />

            {/* Notification Settings */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Confirmation Notifications
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.notifications?.enableEmail || false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: updateNotifications(prev.settings.notifications, {
                        enableEmail: e.target.checked
                      })
                    }
                  }))}
                />
              }
              label="Send Email Confirmation"
              sx={{ mb: 1 }}
            />

            {formData.settings.notifications?.enableEmail && (
              <Box sx={{ ml: 4, mb: 2 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Email Field</InputLabel>
                  <Select
                    value={formData.settings.notifications?.emailField || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: {
                        ...prev.settings,
                        notifications: updateNotifications(prev.settings.notifications, {
                          emailField: e.target.value
                        })
                      }
                    }))}
                    label="Email Field"
                  >
                    {formData.fields
                      .filter(field => field.type === 'email')
                      .map(field => (
                        <MenuItem key={field.id} value={field.name}>
                          {field.label} ({field.name})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Email Subject"
                  value={formData.settings.notifications?.emailTemplate?.subject || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: updateNotifications(prev.settings.notifications, {
                        emailTemplate: {
                          subject: e.target.value,
                          body: prev.settings.notifications?.emailTemplate?.body || 'Thank you for your submission. We have received your form and will process it accordingly.'
                        }
                      })
                    }
                  }))}
                  margin="normal"
                  placeholder="Form Submission Confirmation"
                />

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Email Message"
                  value={formData.settings.notifications?.emailTemplate?.body || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: updateNotifications(prev.settings.notifications, {
                        emailTemplate: {
                          subject: prev.settings.notifications?.emailTemplate?.subject || 'Form Submission Confirmation',
                          body: e.target.value
                        }
                      })
                    }
                  }))}
                  margin="normal"
                  placeholder="Thank you for your submission. We have received your form and will process it accordingly."
                  helperText="You can use variables like {{name}}, {{submissionId}}, {{uniqueId}} in your message"
                />

                {/* Available Variables Section for Email */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Available Variables (use double curly braces):
                  </Typography>
                  
                  {/* System Variables */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="primary">
                      System Variables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {[
                        { variable: 'formTitle', desc: 'Form name' },
                        { variable: 'submissionId', desc: 'Submission ID' },
                        { variable: 'uniqueId', desc: 'Unique ID' },
                        { variable: 'intUniqueId', desc: 'Unique ID (integer)' },
                        { variable: 'submissionTimestamp', desc: 'Submission time' },
                        { variable: 'allFields', desc: 'All form data' }
                      ].map(({ variable, desc }) => (
                        <Chip 
                          key={variable}
                          label={`{{${variable}}}`}
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            const currentMessage = formData.settings.notifications?.emailTemplate?.body || '';
                            const newMessage = currentMessage + `{{${variable}}}`;
                            setFormData(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                notifications: updateNotifications(prev.settings.notifications, {
                                  emailTemplate: {
                                    subject: prev.settings.notifications?.emailTemplate?.subject || 'Form Submission Confirmation',
                                    body: newMessage
                                  }
                                })
                              }
                            }));
                          }}
                          sx={{ cursor: 'pointer' }}
                          title={`Click to add: ${desc}`}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Form Field Variables */}
                  {formData.fields.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="primary">
                        Form Field Variables:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {formData.fields
                          .filter(field => field.type !== 'heading' && field.type !== 'paragraph' && field.type !== 'divider')
                          .map(field => (
                            <Chip 
                              key={field.id}
                              label={field.label + ' (' + field.name + ')'}
                              size="small" 
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                const currentMessage = formData.settings.notifications?.emailTemplate?.body || '';
                                const newMessage = currentMessage + `{{${field.name}}}`;
                                setFormData(prev => ({
                                  ...prev,
                                  settings: {
                                    ...prev.settings,
                                    notifications: updateNotifications(prev.settings.notifications, {
                                      emailTemplate: {
                                        subject: prev.settings.notifications?.emailTemplate?.subject || 'Form Submission Confirmation',
                                        body: newMessage
                                      }
                                    })
                                  }
                                }));
                              }}
                              sx={{ cursor: 'pointer' }}
                              title={`Click to add: ${field.label} value ({{${field.name}}})`}
                            />
                          ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Click any field name to add its variable to your message. Variables will be replaced with actual submitted values.
                      </Typography>
                    </Box>
                  )}

                  {/* Example */}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      Examples:
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      "Hi {'{{field_1752634568489}}'}, your application has been received! Reference: {'{{uniqueId}}'}"
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      "Form submitted: {'{{formTitle}}'}{'\n'}{'{{allFields}}'}"
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Use {'{{allFields}}'} to include all form data automatically.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.notifications?.enableSMS || false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: updateNotifications(prev.settings.notifications, {
                        enableSMS: e.target.checked
                      })
                    }
                  }))}
                />
              }
              label="Send SMS Confirmation"
              sx={{ mb: 1 }}
            />

            {formData.settings.notifications?.enableSMS && (
              <Box sx={{ ml: 4, mb: 2 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Phone Field</InputLabel>
                  <Select
                    value={formData.settings.notifications?.phoneField || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: {
                        ...prev.settings,
                        notifications: updateNotifications(prev.settings.notifications, {
                          phoneField: e.target.value
                        })
                      }
                    }))}
                    label="Phone Field"
                  >
                    {formData.fields
                      .filter(field => field.type === 'tel')
                      .map(field => (
                        <MenuItem key={field.id} value={field.name}>
                          {field.label} ({field.name})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="SMS Message"
                  value={formData.settings.notifications?.smsTemplate?.message || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      notifications: updateNotifications(prev.settings.notifications, {
                        smsTemplate: {
                          message: e.target.value
                        }
                      })
                    }
                  }))}
                  margin="normal"
                  placeholder="Hi {{name}}, thank you for your submission! Your submission ID is {{uniqueId}}."
                  helperText="Keep SMS messages under 160 characters."
                  inputProps={{ maxLength: 160 }}
                />

                {/* Available Variables Section */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">
                    Available Variables (use double curly braces):
                  </Typography>
                  
                  {/* System Variables */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="primary">
                      System Variables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {[
                        { variable: 'formTitle', desc: 'Form name' },
                        { variable: 'submissionId', desc: 'Submission ID' },
                        { variable: 'uniqueId', desc: 'Unique ID' },
                        { variable: 'intUniqueId', desc: 'Unique ID (integer)' },
                        { variable: 'submissionTimestamp', desc: 'Submission time' },
                        { variable: 'allFields', desc: 'All form data' }
                      ].map(({ variable, desc }) => (
                        <Chip 
                          key={variable}
                          label={`{{${variable}}}`}
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            const currentMessage = formData.settings.notifications?.smsTemplate?.message || '';
                            const newMessage = currentMessage + `{{${variable}}}`;
                            setFormData(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings,
                                notifications: updateNotifications(prev.settings.notifications, {
                                  smsTemplate: {
                                    message: newMessage
                                  }
                                })
                              }
                            }));
                          }}
                          sx={{ cursor: 'pointer' }}
                          title={`Click to add: ${desc}`}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Form Field Variables */}
                  {formData.fields.length > 0 && (
                    <Box>
                      <Typography variant="caption" fontWeight="bold" color="primary">
                        Form Field Variables:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {formData.fields
                          .filter(field => field.type !== 'heading' && field.type !== 'paragraph' && field.type !== 'divider')
                          .map(field => (
                            <Chip 
                              key={field.id}
                              label={field.label + ' (' + field.name + ')'}
                              size="small" 
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                const currentMessage = formData.settings.notifications?.smsTemplate?.message || '';
                                const newMessage = currentMessage + `{{${field.name}}}`;
                                setFormData(prev => ({
                                  ...prev,
                                  settings: {
                                    ...prev.settings,
                                    notifications: updateNotifications(prev.settings.notifications, {
                                      smsTemplate: {
                                        message: newMessage
                                      }
                                    })
                                  }
                                }));
                              }}
                              sx={{ cursor: 'pointer' }}
                              title={`Click to add: ${field.label} value ({{${field.name}}})`}
                            />
                          ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Click any field name to add its variable to your message. Variables will be replaced with actual submitted values.
                      </Typography>
                    </Box>
                  )}

                  {/* Example */}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      Examples:
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      "Hi {'{{field_1752634568489}}'}, your application has been received! Reference: {'{{uniqueId}}'}"
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      "Form submitted: {'{{formTitle}}'}{'\n'}{'{{allFields}}'}"
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Use {'{{allFields}}'} to include all form data automatically.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>

          {/* PDF Customization Settings */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              PDF Output Settings
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Customize how submission PDFs appear when users download them
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.pdfSettings?.enablePublicPdf !== false}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      pdfSettings: {
                        ...prev.settings.pdfSettings,
                        enablePublicPdf: e.target.checked
                      }
                    }
                  }))}
                />
              }
              label="Enable Public PDF Access"
              sx={{ mb: 2 }}
            />

            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
              When enabled, users can access their submissions as PDF using their unique ID
            </Typography>

            {formData.settings.pdfSettings?.enablePublicPdf !== false && (
              <Box>
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  PDF Content
                </Typography>

                <TextField
                  fullWidth
                  label="Custom PDF Title"
                  value={formData.settings.pdfSettings?.customTitle || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      pdfSettings: {
                        ...prev.settings.pdfSettings,
                        customTitle: e.target.value
                      }
                    }
                  }))}
                  margin="normal"
                  placeholder="Leave empty to use form title"
                  helperText="Override the title shown in PDF (optional)"
                />

                <TextField
                  fullWidth
                  label="Custom Header"
                  value={formData.settings.pdfSettings?.customHeader || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      pdfSettings: {
                        ...prev.settings.pdfSettings,
                        customHeader: e.target.value
                      }
                    }
                  }))}
                  margin="normal"
                  multiline
                  rows={2}
                  placeholder="Additional text to show at the top of PDF"
                  helperText="Optional header text for the PDF"
                />

                <TextField
                  fullWidth
                  label="Custom Footer"
                  value={formData.settings.pdfSettings?.customFooter || 'BPDAA Management System'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      pdfSettings: {
                        ...prev.settings.pdfSettings,
                        customFooter: e.target.value
                      }
                    }
                  }))}
                  margin="normal"
                  multiline
                  rows={2}
                  helperText="Text to show at the bottom of PDF"
                />

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Visual Styling
                </Typography>

                <TextField
                  label="Primary Color"
                  type="color"
                  value={formData.settings.pdfSettings?.customStyling?.primaryColor || '#007bff'}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      pdfSettings: {
                        ...prev.settings.pdfSettings,
                        customStyling: {
                          ...prev.settings.pdfSettings?.customStyling,
                          primaryColor: e.target.value
                        }
                      }
                    }
                  }))}
                  margin="normal"
                  sx={{ mr: 2, width: '200px' }}
                  helperText="Main color for headers and accents"
                />

                <FormControl margin="normal" sx={{ mr: 2, minWidth: '150px' }}>
                  <InputLabel>Font Family</InputLabel>
                  <Select
                    value={formData.settings.pdfSettings?.customStyling?.fontFamily || 'Arial, sans-serif'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: {
                        ...prev.settings,
                        pdfSettings: {
                          ...prev.settings.pdfSettings,
                          customStyling: {
                            ...prev.settings.pdfSettings?.customStyling,
                            fontFamily: e.target.value
                          }
                        }
                      }
                    }))}
                    label="Font Family"
                  >
                    <MenuItem value="Arial, sans-serif">Arial</MenuItem>
                    <MenuItem value="'Times New Roman', serif">Times New Roman</MenuItem>
                    <MenuItem value="'Courier New', monospace">Courier New</MenuItem>
                    <MenuItem value="'Segoe UI', Tahoma, Geneva, Verdana, sans-serif">Segoe UI</MenuItem>
                    <MenuItem value="Georgia, serif">Georgia</MenuItem>
                  </Select>
                </FormControl>

                <FormControl margin="normal" sx={{ minWidth: '120px' }}>
                  <InputLabel>Font Size</InputLabel>
                  <Select
                    value={formData.settings.pdfSettings?.customStyling?.fontSize || '12px'}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      settings: {
                        ...prev.settings,
                        pdfSettings: {
                          ...prev.settings.pdfSettings,
                          customStyling: {
                            ...prev.settings.pdfSettings?.customStyling,
                            fontSize: e.target.value
                          }
                        }
                      }
                    }))}
                    label="Font Size"
                  >
                    <MenuItem value="10px">10px</MenuItem>
                    <MenuItem value="11px">11px</MenuItem>
                    <MenuItem value="12px">12px</MenuItem>
                    <MenuItem value="14px">14px</MenuItem>
                    <MenuItem value="16px">16px</MenuItem>
                  </Select>
                </FormControl>

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Content Layout
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings.pdfSettings?.showPaymentDetails !== false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            showPaymentDetails: e.target.checked
                          }
                        }
                      }))}
                    />
                  }
                  label="Show Payment Details in PDF"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings.pdfSettings?.fieldsLayout?.hideEmptyFields !== false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            fieldsLayout: {
                              ...prev.settings.pdfSettings?.fieldsLayout,
                              hideEmptyFields: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Hide Empty Fields"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings.pdfSettings?.fieldsLayout?.showFieldLabels !== false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            fieldsLayout: {
                              ...prev.settings.pdfSettings?.fieldsLayout,
                              showFieldLabels: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Show Field Labels"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings.pdfSettings?.fieldsLayout?.showFormData !== false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            fieldsLayout: {
                              ...prev.settings.pdfSettings?.fieldsLayout,
                              showFormData: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Show Form Data Section"
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings.pdfSettings?.fieldsLayout?.showSubmissionInfo !== false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            fieldsLayout: {
                              ...prev.settings.pdfSettings?.fieldsLayout,
                              showSubmissionInfo: e.target.checked
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Show Submission Information Section"
                />

                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Custom HTML Content
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.settings.pdfSettings?.htmlContent?.enabled || false}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            htmlContent: {
                              ...prev.settings.pdfSettings?.htmlContent,
                              enabled: e.target.checked,
                              content: prev.settings.pdfSettings?.htmlContent?.content || '',
                              enableVariables: prev.settings.pdfSettings?.htmlContent?.enableVariables || false,
                              position: prev.settings.pdfSettings?.htmlContent?.position || 'before-fields'
                            }
                          }
                        }
                      }))}
                    />
                  }
                  label="Enable Custom HTML Content in PDF"
                  sx={{ mb: 2 }}
                />

                {formData.settings.pdfSettings?.htmlContent?.enabled && (
                  <Box>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Content Position</InputLabel>
                      <Select<string>
                        value={formData.settings.pdfSettings?.htmlContent?.position || 'before-fields'}
                        onChange={(e) => {
                          const position = e.target.value as 'header' | 'footer' | 'before-fields' | 'after-fields';
                          setFormData(prev => ({
                            ...prev,
                            settings: {
                              ...prev.settings,
                              pdfSettings: {
                                ...prev.settings.pdfSettings,
                                htmlContent: {
                                  ...prev.settings.pdfSettings?.htmlContent,
                                  position: position
                                }
                              }
                            }
                          }));
                        }}
                        label="Content Position"
                      >
                        <MenuItem value="header">Header (Top of PDF)</MenuItem>
                        <MenuItem value="before-fields">Before Form Data</MenuItem>
                        <MenuItem value="after-fields">After Form Data</MenuItem>
                        <MenuItem value="footer">Footer (Bottom of PDF)</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.settings.pdfSettings?.htmlContent?.enableVariables || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            settings: {
                              ...prev.settings,
                              pdfSettings: {
                                ...prev.settings.pdfSettings,
                                htmlContent: {
                                  ...prev.settings.pdfSettings?.htmlContent,
                                  enableVariables: e.target.checked
                                }
                              }
                            }
                          }))}
                        />
                      }
                      label="Enable Variable Replacement"
                      sx={{ mb: 2, mt: 1 }}
                    />

                    <TextField
                      fullWidth
                      label="HTML Content"
                      multiline
                      rows={6}
                      value={formData.settings.pdfSettings?.htmlContent?.content || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        settings: {
                          ...prev.settings,
                          pdfSettings: {
                            ...prev.settings.pdfSettings,
                            htmlContent: {
                              ...prev.settings.pdfSettings?.htmlContent,
                              content: e.target.value
                            }
                          }
                        }
                      }))}
                      placeholder="Enter HTML content for PDF..."
                      helperText="You can use HTML tags and variables (if enabled above)"
                      margin="normal"
                      sx={{ mb: 2 }}
                    />

                    {formData.settings.pdfSettings?.htmlContent?.enableVariables && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom color="primary">
                          Available Variables (click to insert):
                        </Typography>
                        
                        {/* System Variables */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" fontWeight="bold" color="text.secondary">
                            System Variables:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {[
                              { variable: 'formTitle', desc: 'Form name' },
                              { variable: 'submissionId', desc: 'Submission ID' },
                              { variable: 'uniqueId', desc: 'Unique ID' },
                              { variable: 'intUniqueId', desc: 'Unique ID (integer)' },
                              { variable: 'submissionTimestamp', desc: 'Submission time' },
                              { variable: 'submissionDate', desc: 'Submission date' },
                              { variable: 'allFields', desc: 'All form data' }
                            ].map(({ variable, desc }) => (
                              <Chip 
                                key={variable}
                                label={`{{${variable}}}`}
                                size="small" 
                                variant="outlined"
                                onClick={() => {
                                  const currentContent = formData.settings.pdfSettings?.htmlContent?.content || '';
                                  const newContent = currentContent + `{{${variable}}}`;
                                  setFormData(prev => ({
                                    ...prev,
                                    settings: {
                                      ...prev.settings,
                                      pdfSettings: {
                                        ...prev.settings.pdfSettings,
                                        htmlContent: {
                                          ...prev.settings.pdfSettings?.htmlContent,
                                          content: newContent
                                        }
                                      }
                                    }
                                  }));
                                }}
                                sx={{ cursor: 'pointer' }}
                                title={`Click to add: ${desc}`}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Form Field Variables */}
                        {formData.fields && formData.fields.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary">
                              Form Field Variables:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {formData.fields
                                .filter(f => f.type !== 'heading' && f.type !== 'paragraph' && f.type !== 'divider' && f.type !== 'html')
                                .map(f => (
                                  <Chip 
                                    key={f.id}
                                    label={`${f.label} (${f.name})`}
                                    size="small" 
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                      const currentContent = formData.settings.pdfSettings?.htmlContent?.content || '';
                                      const newContent = currentContent + `{{${f.name}}}`;
                                      setFormData(prev => ({
                                        ...prev,
                                        settings: {
                                          ...prev.settings,
                                          pdfSettings: {
                                            ...prev.settings.pdfSettings,
                                            htmlContent: {
                                              ...prev.settings.pdfSettings?.htmlContent,
                                              content: newContent
                                            }
                                          }
                                        }
                                      }));
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                    title={`Click to add: ${f.label} value ({{${f.name}}})`}
                                  />
                                ))}
                            </Box>
                          </Box>
                        )}

                        {/* Example */}
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="caption" fontWeight="bold">
                            Examples:
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                            {`<h3>Submission Summary</h3><p><strong>Form:</strong> {{formTitle}}</p>`}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                            {`<div style="border: 1px solid #ccc; padding: 10px; margin: 10px 0;"><strong>ID:</strong> {{uniqueId}}<br/>{{allFields}}</div>`}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="caption">
                        <strong>Note:</strong> HTML content will be rendered in the PDF at the specified position. 
                        Use variables to include dynamic content from the submission data.
                      </Typography>
                    </Alert>
                  </Box>
                )}

                <Alert severity="info" sx={{ mt: 2 }}>
                  PDF settings only affect the public PDF that users can access with their unique submission ID.
                  Admin exports will continue to show all data based on your user permissions.
                </Alert>
              </Box>
            )}
          </Paper>

          {/* Form Fields */}
          <Paper sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Form Fields ({formData.fields.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddField}
              >
                Add Field
              </Button>
            </Box>

            {/* Admin Table Display Info */}
            {(() => {
              const adminTableFields = formData.fields.filter(field => 
                field.displayInAdminTable && !['html', 'heading', 'paragraph', 'divider'].includes(field.type)
              );
              
              if (adminTableFields.length > 0) {
                return (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Admin Submissions Table Display
                    </Typography>
                    <Typography variant="body2">
                      {adminTableFields.length} field(s) configured to show as columns: {' '}
                      {adminTableFields.map(field => field.label || field.name).join(', ')}
                    </Typography>
                  </Alert>
                );
              }
              
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    No fields are currently configured to display in the admin submissions table. 
                    Enable "Show in Admin Submissions Table" for specific fields to create custom table columns.
                  </Typography>
                </Alert>
              );
            })()}

            {formData.fields.length === 0 ? (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  No fields added yet
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddField}
                >
                  Add Your First Field
                </Button>
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" gap={2}>
                {formData.fields
                  .sort((a, b) => a.order - b.order)
                  .map((field, index) => (
                  <Card key={field.id} variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box display="flex" flexDirection="column">
                          <IconButton 
                            size="small" 
                            onClick={() => moveFieldUp(field.id)}
                            disabled={index === 0}
                          >
                            <ArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => moveFieldDown(field.id)}
                            disabled={index === formData.fields.length - 1}
                          >
                            <ArrowDownIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <IconButton size="small" sx={{ cursor: 'grab' }}>
                          <DragIcon />
                        </IconButton>
                        
                        <Box flex={1}>
                          <Typography variant="subtitle1">
                            {field.label || 'Untitled Field'}
                          </Typography>
                          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip 
                              label={fieldTypes.find(t => t.value === field.type)?.label || field.type}
                              size="small"
                            />
                            {field.required && (
                              <Chip label="Required" size="small" color="error" />
                            )}
                            {field.layout && (
                              <Chip 
                                label={`Width: ${Math.round((field.layout.width / 12) * 100)}%`}
                                size="small" 
                                color="info"
                              />
                            )}
                            {field.conditional?.dependsOn && (
                              <Chip label="Conditional" size="small" color="warning" />
                            )}
                            {field.visibility?.level === 'admin-only' && (
                              <Chip 
                                label="Admin Only" 
                                size="small" 
                                color="secondary"
                                sx={{ bgcolor: 'orange', color: 'white' }}
                              />
                            )}
                            {field.visibility?.level === 'hidden' && (
                              <Chip 
                                label="Hidden" 
                                size="small" 
                                color="default"
                                sx={{ bgcolor: 'grey.500', color: 'white' }}
                              />
                            )}
                            {field.displayInAdminTable && !['html', 'heading', 'paragraph', 'divider'].includes(field.type) && (
                              <Chip 
                                label="Admin Table" 
                                size="small" 
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() => handleEditField(field)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteField(field.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Form Preview */}
        <Box width={400}>
          <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            
            <Box border={1} borderColor="divider" borderRadius={1} p={2} bgcolor="background.default">
              <Typography variant="h5" gutterBottom>
                {formData.title || 'Untitled Form'}
              </Typography>
              
              {formData.description && (
                <Typography variant="body2" color="textSecondary" paragraph>
                  {formData.description}
                </Typography>
              )}

              {formData.settings.requirePayment && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Payment required: ৳{formData.settings.amount || 0}
                </Alert>
              )}

              <Box 
                sx={{ 
                  display: 'flex',
                  flexWrap: 'wrap',
                  margin: '-4px', // Negative margin to offset padding
                  '& > *': {
                    padding: '4px', // Padding instead of gap for better control
                    boxSizing: 'border-box'
                  }
                }}
              >
                {formData.fields
                  .sort((a, b) => a.order - b.order)
                  .map((field) => (
                  <Box 
                    key={field.id}
                    sx={{ 
                      width: field.layout?.width ? `${(field.layout.width / 12) * 100}%` : '100%',
                      '@media (max-width: 600px)': {
                        width: '100%'
                      }
                    }}
                  >
                    <Box>
                      {field.type === 'heading' ? (
                        <Typography 
                          variant={field.styling?.headingLevel || 'h2'}
                          sx={{
                            textAlign: field.styling?.textAlign || 'left',
                            fontSize: field.styling?.fontSize,
                            fontWeight: field.styling?.fontWeight || 'normal',
                            color: field.styling?.color,
                            backgroundColor: field.styling?.backgroundColor,
                            padding: field.styling?.padding,
                            marginTop: field.styling?.marginTop,
                            marginBottom: field.styling?.marginBottom,
                          }}
                        >
                          {field.label || 'Heading Text'}
                        </Typography>
                      ) : field.type === 'paragraph' ? (
                        <Typography 
                          variant="body1"
                          sx={{
                            textAlign: field.styling?.textAlign || 'left',
                            fontSize: field.styling?.fontSize,
                            fontWeight: field.styling?.fontWeight || 'normal',
                            color: field.styling?.color,
                            backgroundColor: field.styling?.backgroundColor,
                            padding: field.styling?.padding,
                            marginTop: field.styling?.marginTop,
                            marginBottom: field.styling?.marginBottom,
                          }}
                        >
                          {field.label || 'Paragraph text content goes here...'}
                        </Typography>
                      ) : field.type === 'divider' ? (
                        <Divider 
                          sx={{
                            backgroundColor: field.styling?.color,
                            marginTop: field.styling?.marginTop,
                            marginBottom: field.styling?.marginBottom,
                          }}
                        />
                      ) : field.type === 'html' ? (
                        <HtmlContentField
                          field={field}
                          formData={formData}
                          submissionData={null}
                          isPreview={true}
                        />
                      ) : (
                        <>
                          <Typography variant="body2" gutterBottom>
                            {field.label}
                            {field.required && <span style={{ color: 'red' }}> *</span>}
                          </Typography>
                          
                          {field.type === 'textarea' ? (
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              placeholder={field.placeholder}
                              size="small"
                              disabled
                            />
                          ) : field.type === 'select' ? (
                            <FormControl fullWidth size="small">
                              <Select disabled>
                                {field.options?.map((option, idx) => (
                                  <MenuItem key={idx} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : field.type === 'radio' ? (
                            <Box>
                              {field.options?.map((option, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={<input type="radio" disabled />}
                                  label={option}
                                  disabled
                                />
                              ))}
                            </Box>
                          ) : field.type === 'checkbox' ? (
                            <Box>
                              {field.options?.map((option, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={<input type="checkbox" disabled />}
                                  label={option}
                                  disabled
                                />
                              ))}
                            </Box>
                          ) : (
                            <TextField
                              fullWidth
                              type={field.type}
                              placeholder={field.placeholder}
                              size="small"
                              disabled
                            />
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              {formData.fields.length === 0 && (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  No fields added yet
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Field Editor Dialog */}
      <FieldEditorDialog
        open={fieldDialog.open}
        field={fieldDialog.field}
        isEdit={fieldDialog.isEdit}
        onSave={handleSaveField}
        onClose={() => setFieldDialog({ open: false, field: null, isEdit: false })}
        allFields={formData.fields}
      />
    </Box>
  );
};

// Field Editor Dialog Component
interface FieldEditorDialogProps {
  open: boolean;
  field: FormField | null;
  isEdit: boolean;
  onSave: (field: FormField) => void;
  onClose: () => void;
  allFields: FormField[];
}

const FieldEditorDialog: React.FC<FieldEditorDialogProps> = ({
  open,
  field,
  isEdit,
  onSave,
  onClose,
  allFields
}) => {
  const [editField, setEditField] = useState<FormField | null>(null);

  useEffect(() => {
    if (field) {
      setEditField({ ...field });
    }
  }, [field]);

  const handleSave = () => {
    if (editField) {
      onSave(editField);
    }
  };

  if (!editField) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Field' : 'Add New Field'}
      </DialogTitle>
      
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField
            fullWidth
            label="Field Label"
            value={editField.label}
            onChange={(e) => setEditField(prev => prev ? { ...prev, label: e.target.value } : null)}
            required
          />

          <FormControl fullWidth>
            <InputLabel>Field Type</InputLabel>
            <Select
              value={editField.type}
              onChange={(e) => setEditField(prev => prev ? { ...prev, type: e.target.value } : null)}
            >
              {fieldTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Placeholder Text"
            value={editField.placeholder || ''}
            onChange={(e) => setEditField(prev => prev ? { ...prev, placeholder: e.target.value } : null)}
          />

          <FormControlLabel
            control={
              <Switch
                checked={editField.required}
                onChange={(e) => setEditField(prev => prev ? { ...prev, required: e.target.checked } : null)}
              />
            }
            label="Required Field"
          />

          <FormControlLabel
            control={
              <Switch
                checked={editField.editable !== false && !(editField.pricing?.enabled)}
                disabled={editField.pricing?.enabled}
                onChange={(e) => setEditField(prev => prev ? { ...prev, editable: e.target.checked } : null)}
              />
            }
            label="Allow Editing After Submission"
          />

          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: -1, mb: 2 }}>
            {editField.pricing?.enabled 
              ? "Pricing fields cannot be edited after submission to maintain payment integrity"
              : "When disabled, users cannot edit this field after submitting the form"
            }
          </Typography>

          {/* Admin Table Display Option */}
          {!['html', 'heading', 'paragraph', 'divider'].includes(editField.type) && (
            <FormControlLabel
              control={
                <Switch
                  checked={editField.displayInAdminTable || false}
                  onChange={(e) => setEditField(prev => prev ? { ...prev, displayInAdminTable: e.target.checked } : null)}
                />
              }
              label="Show in Admin Submissions Table"
            />
          )}

          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: editField.displayInAdminTable ? -1 : 0, mb: 2 }}>
            {!['html', 'heading', 'paragraph', 'divider'].includes(editField.type) && editField.displayInAdminTable ?
              "This field will appear as a column in the admin submissions table for easy overview" :
              !['html', 'heading', 'paragraph', 'divider'].includes(editField.type) ?
              "When enabled, this field will be displayed as a column in the admin submissions table" : ""
            }
          </Typography>

          {(editField.type === 'select' || editField.type === 'radio' || editField.type === 'checkbox') && (
            <TextField
              fullWidth
              label="Options (one per line)"
              multiline
              rows={4}
              value={editField.options?.join('\n') || ''}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                options: e.target.value.split('\n').filter(opt => opt.trim())
              } : null)}
              placeholder="Option 1&#10;Option 2&#10;Option 3"
            />
          )}

          {/* HTML Content Editor */}
          {editField.type === 'html' && (
            <Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                HTML Content Settings
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={editField.htmlContent?.enableVariables || false}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      htmlContent: {
                        ...prev.htmlContent,
                        enableVariables: e.target.checked,
                        content: prev.htmlContent?.content || ''
                      }
                    } : null)}
                  />
                }
                label="Enable Variable Replacement"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="HTML Content"
                multiline
                rows={8}
                value={editField.htmlContent?.content || ''}
                onChange={(e) => setEditField(prev => prev ? {
                  ...prev,
                  htmlContent: {
                    ...prev.htmlContent,
                    content: e.target.value,
                    enableVariables: prev.htmlContent?.enableVariables || false
                  }
                } : null)}
                placeholder="Enter your HTML content here..."
                helperText="You can use HTML tags and variables (if enabled above)"
                sx={{ mb: 2 }}
              />

              {editField.htmlContent?.enableVariables && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Available Variables (click to insert):
                  </Typography>
                  
                  {/* System Variables */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary">
                      System Variables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {[
                        { variable: 'formTitle', desc: 'Form name' },
                        { variable: 'submissionId', desc: 'Submission ID' },
                        { variable: 'uniqueId', desc: 'Unique ID' },
                        { variable: 'intUniqueId', desc: 'Unique ID (integer)' },
                        { variable: 'submissionTimestamp', desc: 'Submission time' },
                        { variable: 'submissionDate', desc: 'Submission date' },
                        { variable: 'allFields', desc: 'All form data' }
                      ].map(({ variable, desc }) => (
                        <Chip 
                          key={variable}
                          label={`{{${variable}}}`}
                          size="small" 
                          variant="outlined"
                          onClick={() => {
                            const currentContent = editField.htmlContent?.content || '';
                            const newContent = currentContent + `{{${variable}}}`;
                            setEditField(prev => prev ? {
                              ...prev,
                              htmlContent: {
                                ...prev.htmlContent,
                                content: newContent,
                                enableVariables: prev.htmlContent?.enableVariables || false
                              }
                            } : null);
                          }}
                          sx={{ cursor: 'pointer' }}
                          title={`Click to add: ${desc}`}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Form Field Variables */}
                  {allFields && allFields.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary">
                        Form Field Variables:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {allFields
                          .filter(f => f.type !== 'heading' && f.type !== 'paragraph' && f.type !== 'divider' && f.type !== 'html' && f.id !== editField.id)
                          .map(f => (
                            <Chip 
                              key={f.id}
                              label={`${f.label} (${f.name})`}
                              size="small" 
                              variant="outlined"
                              color="secondary"
                              onClick={() => {
                                const currentContent = editField.htmlContent?.content || '';
                                const newContent = currentContent + `{{${f.name}}}`;
                                setEditField(prev => prev ? {
                                  ...prev,
                                  htmlContent: {
                                    ...prev.htmlContent,
                                    content: newContent,
                                    enableVariables: prev.htmlContent?.enableVariables || false
                                  }
                                } : null);
                              }}
                              sx={{ cursor: 'pointer' }}
                              title={`Click to add: ${f.label} value ({{${f.name}}})`}
                            />
                          ))}
                      </Box>
                    </Box>
                  )}

                  {/* Example */}
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      Examples:
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      {`<h3>Welcome {{name}}!</h3><p>Your submission ID is: {{uniqueId}}</p>`}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                      {`<div style="background: #f0f0f0; padding: 10px;"><strong>Form:</strong> {{formTitle}}<br/>{{allFields}}</div>`}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Note:</strong> HTML content fields are display-only and don't collect user input. 
                  They're perfect for instructions, formatted text, or dynamic content using variables.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Layout Settings */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Layout Settings
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Field Width</InputLabel>
            <Select
              value={editField.layout?.width || 12}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                layout: {
                  ...prev.layout,
                  width: e.target.value as number,
                  breakpoint: prev.layout?.breakpoint || 'sm'
                }
              } : null)}
            >
              <MenuItem value={12}>Full Width (100%)</MenuItem>
              <MenuItem value={6}>Half Width (50%)</MenuItem>
              <MenuItem value={4}>Third Width (33%)</MenuItem>
              <MenuItem value={3}>Quarter Width (25%)</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>
              Field Width: {Math.round(((editField.layout?.width || 12) / 12) * 100)}%
            </Typography>
            <Slider
              value={editField.layout?.width || 12}
              onChange={(_, value) => setEditField(prev => prev ? {
                ...prev,
                layout: {
                  ...prev.layout,
                  width: value as number,
                  breakpoint: prev.layout?.breakpoint || 'sm'
                }
              } : null)}
              min={3}
              max={12}
              step={3}
              marks={[
                { value: 3, label: '25%' },
                { value: 6, label: '50%' },
                { value: 9, label: '75%' },
                { value: 12, label: '100%' }
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round((value / 12) * 100)}%`}
            />
          </Box>

          {/* Visibility Settings */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Field Visibility
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Visibility Level</InputLabel>
            <Select
              value={editField.visibility?.level || 'public'}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                visibility: {
                  ...prev.visibility,
                  level: e.target.value as 'public' | 'admin-only' | 'hidden'
                }
              } : null)}
            >
              <MenuItem value="public">
                <Box>
                  <Typography variant="body2" fontWeight="bold">Public</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Everyone can see and fill this field
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="admin-only">
                <Box>
                  <Typography variant="body2" fontWeight="bold">Admin Only</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Only admins can see and edit this field
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="hidden">
                <Box>
                  <Typography variant="body2" fontWeight="bold">Hidden</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Field is hidden from all users
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {editField.visibility?.level === 'admin-only' && (
            <FormControlLabel
              control={
                <Switch
                  checked={editField.visibility?.publicCanView || false}
                  onChange={(e) => setEditField(prev => prev ? {
                    ...prev,
                    visibility: {
                      ...prev.visibility,
                      publicCanView: e.target.checked
                    }
                  } : null)}
                />
              }
              label="Allow public users to view (but not edit) this field"
            />
          )}

          {(editField.visibility?.level === 'admin-only' || editField.visibility?.level === 'hidden') && (
            <TextField
              fullWidth
              label="Visibility Description (optional)"
              value={editField.visibility?.description || ''}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                visibility: {
                  ...prev.visibility,
                  description: e.target.value
                }
              } : null)}
              placeholder="Why is this field restricted? (e.g., 'Contains sensitive information')"
              multiline
              rows={2}
            />
          )}

          {/* Styling Settings (for text/heading fields) */}
          {(editField.type === 'heading' || editField.type === 'paragraph' || editField.type === 'divider') && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Styling Options
              </Typography>
              
              {editField.type === 'heading' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Heading Level</InputLabel>
                  <Select
                    value={editField.styling?.headingLevel || 'h2'}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        headingLevel: e.target.value as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
                      }
                    } : null)}
                  >
                    <MenuItem value="h1">H1 - Main Title</MenuItem>
                    <MenuItem value="h2">H2 - Section Title</MenuItem>
                    <MenuItem value="h3">H3 - Subsection</MenuItem>
                    <MenuItem value="h4">H4 - Small Heading</MenuItem>
                    <MenuItem value="h5">H5 - Minor Heading</MenuItem>
                    <MenuItem value="h6">H6 - Smallest Heading</MenuItem>
                  </Select>
                </FormControl>
              )}

              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <FormControl fullWidth>
                    <InputLabel>Text Alignment</InputLabel>
                    <Select
                      value={editField.styling?.textAlign || 'left'}
                      onChange={(e) => setEditField(prev => prev ? {
                        ...prev,
                        styling: {
                          ...prev.styling,
                          textAlign: e.target.value as 'left' | 'center' | 'right'
                        }
                      } : null)}
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="center">Center</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Font Weight</InputLabel>
                    <Select
                      value={editField.styling?.fontWeight || 'normal'}
                      onChange={(e) => setEditField(prev => prev ? {
                        ...prev,
                        styling: {
                          ...prev.styling,
                          fontWeight: e.target.value as 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'
                        }
                      } : null)}
                    >
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="bold">Bold</MenuItem>
                      <MenuItem value="300">Light</MenuItem>
                      <MenuItem value="500">Medium</MenuItem>
                      <MenuItem value="700">Bold</MenuItem>
                      <MenuItem value="900">Extra Bold</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Font Size"
                    value={editField.styling?.fontSize || ''}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        fontSize: e.target.value
                      }
                    } : null)}
                    placeholder="16px, 1.2rem, large"
                    helperText="CSS font-size value"
                  />

                  <TextField
                    fullWidth
                    label="Text Color"
                    type="color"
                    value={editField.styling?.color || '#000000'}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        color: e.target.value
                      }
                    } : null)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Background Color"
                    type="color"
                    value={editField.styling?.backgroundColor || '#ffffff'}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        backgroundColor: e.target.value
                      }
                    } : null)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    label="Padding"
                    value={editField.styling?.padding || ''}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        padding: e.target.value
                      }
                    } : null)}
                    placeholder="8px, 1rem"
                    helperText="CSS padding value"
                  />
                </Box>

                <Box display="flex" gap={2}>
                  <TextField
                    fullWidth
                    label="Margin Top"
                    value={editField.styling?.marginTop || ''}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        marginTop: e.target.value
                      }
                    } : null)}
                    placeholder="16px, 1rem"
                    helperText="Space above element"
                  />

                  <TextField
                    fullWidth
                    label="Margin Bottom"
                    value={editField.styling?.marginBottom || ''}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      styling: {
                        ...prev.styling,
                        marginBottom: e.target.value
                      }
                    } : null)}
                    placeholder="16px, 1rem"
                    helperText="Space below element"
                  />
                </Box>
              </Box>
            </>
          )}

          {/* Option-Based Pricing Settings */}
          {(editField.type === 'select' || editField.type === 'radio' || editField.type === 'checkbox') && editField.options && editField.options.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Option-Based Pricing (Optional)
              </Typography>
              
              <FormControlLabel
                control={
                  <input
                    type="checkbox"
                    checked={editField.pricing?.enabled || false}
                    onChange={(e) => setEditField(prev => prev ? {
                      ...prev,
                      pricing: {
                        enabled: e.target.checked,
                        options: prev.pricing?.options || {}
                      }
                    } : null)}
                  />
                }
                label="Enable different prices for different options"
              />

              {editField.pricing?.enabled && (
                <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.main', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: 'warning.contrastText', fontWeight: 'medium' }}>
                    ⚠️ Pricing fields are automatically non-editable after submission to maintain payment integrity
                  </Typography>
                </Box>
              )}

              {editField.pricing?.enabled && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Set individual prices for each option. These will be added to the base form price.
                  </Typography>
                  
                  {editField.options.map((option, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Typography variant="body2" sx={{ minWidth: '150px' }}>
                        {option}:
                      </Typography>
                      <TextField
                        type="number"
                        label="Price"
                        size="small"
                        value={editField.pricing?.options?.[option] || ''}
                        onChange={(e) => setEditField(prev => prev ? {
                          ...prev,
                          pricing: {
                            ...prev.pricing!,
                            options: {
                              ...prev.pricing?.options,
                              [option]: e.target.value ? parseFloat(e.target.value) : 0
                            }
                          }
                        } : null)}
                        placeholder="0.00"
                        inputProps={{ 
                          min: 0, 
                          step: 0.01,
                          style: { textAlign: 'right' }
                        }}
                        sx={{ width: '120px' }}
                      />
                    </Box>
                  ))}
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="caption">
                      Example: If base form price is ৳10 and user selects an option priced at ৳5, 
                      total will be ৳15. For checkboxes, all selected option prices are added.
                    </Typography>
                  </Alert>
                </Box>
              )}
            </>
          )}

          {/* Conditional Field Settings */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Conditional Logic (Optional)
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>Depends on Field</InputLabel>
            <Select
              value={editField.conditional?.dependsOn || ''}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                conditional: {
                  ...prev.conditional,
                  dependsOn: e.target.value,
                  showWhen: prev.conditional?.showWhen || []
                }
              } : null)}
            >
              <MenuItem value="">
                <em>No dependency</em>
              </MenuItem>
              {allFields
                .filter((field: FormField) => field.id !== editField.id && field.order < editField.order)
                .filter((field: FormField) => ['select', 'radio', 'checkbox'].includes(field.type))
                .map((field: FormField) => (
                  <MenuItem key={field.id} value={field.id}>
                    {field.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {editField.conditional?.dependsOn && (
            <TextField
              fullWidth
              label="Show when values (comma separated)"
              value={editField.conditional?.showWhen?.join(', ') || ''}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                conditional: {
                  ...prev.conditional!,
                  showWhen: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                }
              } : null)}
              placeholder="Yes, Option 1, Other"
              helperText="This field will only show when the selected field has these values"
            />
          )}

          {/* Basic Validation Settings */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Basic Validation
          </Typography>

          {(editField.type === 'text' || editField.type === 'textarea' || editField.type === 'email') && (
            <Box display="flex" gap={2}>
              <TextField
                label="Min Length"
                type="number"
                value={editField.validation?.min || ''}
                onChange={(e) => setEditField(prev => prev ? {
                  ...prev,
                  validation: {
                    ...prev.validation,
                    min: e.target.value ? parseInt(e.target.value) : undefined
                  }
                } : null)}
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
              <TextField
                label="Max Length"
                type="number"
                value={editField.validation?.max || ''}
                onChange={(e) => setEditField(prev => prev ? {
                  ...prev,
                  validation: {
                    ...prev.validation,
                    max: e.target.value ? parseInt(e.target.value) : undefined
                  }
                } : null)}
                sx={{ flex: 1 }}
                inputProps={{ min: 0 }}
              />
            </Box>
          )}

          {editField.type === 'number' && (
            <Box display="flex" gap={2}>
              <TextField
                label="Min Value"
                type="number"
                value={editField.validation?.min || ''}
                onChange={(e) => setEditField(prev => prev ? {
                  ...prev,
                  validation: {
                    ...prev.validation,
                    min: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                } : null)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Max Value"
                type="number"
                value={editField.validation?.max || ''}
                onChange={(e) => setEditField(prev => prev ? {
                  ...prev,
                  validation: {
                    ...prev.validation,
                    max: e.target.value ? parseFloat(e.target.value) : undefined
                  }
                } : null)}
                sx={{ flex: 1 }}
              />
            </Box>
          )}

          {(editField.type === 'text' || editField.type === 'textarea' || editField.type === 'email') && (
            <TextField
              fullWidth
              label="Pattern (RegEx)"
              value={editField.validation?.pattern || ''}
              onChange={(e) => setEditField(prev => prev ? {
                ...prev,
                validation: {
                  ...prev.validation,
                  pattern: e.target.value
                }
              } : null)}
              placeholder="^[A-Za-z]+$"
              helperText="Regular expression pattern for validation"
              sx={{ mt: 2 }}
            />
          )}

          {/* Custom Validation Settings */}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Advanced Validation Rules
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={editField.validation?.custom?.enabled || false}
                onChange={(e) => setEditField(prev => prev ? {
                  ...prev,
                  validation: {
                    ...prev.validation,
                    custom: {
                      enabled: e.target.checked,
                      rules: prev.validation?.custom?.rules || []
                    }
                  }
                } : null)}
              />
            }
            label="Enable Custom Validation Rules"
          />

          {editField.validation?.custom?.enabled && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Add custom validation rules for this field
              </Typography>

              {editField.validation.custom.rules.map((rule, index) => (
                <Paper key={rule.id} sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">
                      Rule #{index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setEditField(prev => prev ? {
                        ...prev,
                        validation: {
                          ...prev.validation,
                          custom: {
                            ...prev.validation?.custom!,
                            rules: prev.validation?.custom?.rules.filter((_, i) => i !== index) || []
                          }
                        }
                      } : null)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Validation Type</InputLabel>
                    <Select
                      value={rule.type}
                      onChange={(e) => {
                        const newRules = [...(editField.validation?.custom?.rules || [])];
                        newRules[index] = { ...rule, type: e.target.value as any };
                        setEditField(prev => prev ? {
                          ...prev,
                          validation: {
                            ...prev.validation,
                            custom: {
                              ...prev.validation?.custom!,
                              rules: newRules
                            }
                          }
                        } : null);
                      }}
                    >
                      <MenuItem value="compare_dates">Compare with Date Field</MenuItem>
                      <MenuItem value="compare_numbers">Compare with Number Field</MenuItem>
                      <MenuItem value="min_length">Minimum Length</MenuItem>
                      <MenuItem value="max_length">Maximum Length</MenuItem>
                      <MenuItem value="custom_pattern">Custom Pattern</MenuItem>
                      <MenuItem value="dependent_value">Dependent Value Validation</MenuItem>
                    </Select>
                  </FormControl>

                  {(rule.type === 'compare_dates' || rule.type === 'compare_numbers') && (
                    <>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Compare with Field</InputLabel>
                        <Select
                          value={rule.compareField || ''}
                          onChange={(e) => {
                            const newRules = [...(editField.validation?.custom?.rules || [])];
                            newRules[index] = { ...rule, compareField: e.target.value };
                            setEditField(prev => prev ? {
                              ...prev,
                              validation: {
                                ...prev.validation,
                                custom: {
                                  ...prev.validation?.custom!,
                                  rules: newRules
                                }
                              }
                            } : null);
                          }}
                        >
                          {allFields
                            .filter(f => f.id !== editField.id)
                            .filter(f => rule.type === 'compare_dates' ? f.type === 'date' : f.type === 'number')
                            .map(field => (
                              <MenuItem key={field.id} value={field.id}>
                                {field.label}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Comparison</InputLabel>
                        <Select
                          value={rule.compareOperator || 'greater_than'}
                          onChange={(e) => {
                            const newRules = [...(editField.validation?.custom?.rules || [])];
                            newRules[index] = { ...rule, compareOperator: e.target.value as any };
                            setEditField(prev => prev ? {
                              ...prev,
                              validation: {
                                ...prev.validation,
                                custom: {
                                  ...prev.validation?.custom!,
                                  rules: newRules
                                }
                              }
                            } : null);
                          }}
                        >
                          <MenuItem value="greater_than">Greater Than</MenuItem>
                          <MenuItem value="greater_equal">Greater Than or Equal</MenuItem>
                          <MenuItem value="less_than">Less Than</MenuItem>
                          <MenuItem value="less_equal">Less Than or Equal</MenuItem>
                          <MenuItem value="equal">Equal To</MenuItem>
                          <MenuItem value="not_equal">Not Equal To</MenuItem>
                        </Select>
                      </FormControl>
                    </>
                  )}

                  {(rule.type === 'min_length' || rule.type === 'max_length') && (
                    <TextField
                      fullWidth
                      label="Length Value"
                      type="number"
                      value={rule.value || ''}
                      onChange={(e) => {
                        const newRules = [...(editField.validation?.custom?.rules || [])];
                        newRules[index] = { ...rule, value: parseInt(e.target.value) || 0 };
                        setEditField(prev => prev ? {
                          ...prev,
                          validation: {
                            ...prev.validation,
                            custom: {
                              ...prev.validation?.custom!,
                              rules: newRules
                            }
                          }
                        } : null);
                      }}
                      sx={{ mb: 2 }}
                      inputProps={{ min: 0 }}
                    />
                  )}

                  {rule.type === 'custom_pattern' && (
                    <TextField
                      fullWidth
                      label="Regular Expression Pattern"
                      value={rule.value || ''}
                      onChange={(e) => {
                        const newRules = [...(editField.validation?.custom?.rules || [])];
                        newRules[index] = { ...rule, value: e.target.value };
                        setEditField(prev => prev ? {
                          ...prev,
                          validation: {
                            ...prev.validation,
                            custom: {
                              ...prev.validation?.custom!,
                              rules: newRules
                            }
                          }
                        } : null);
                      }}
                      sx={{ mb: 2 }}
                      placeholder="^[A-Za-z0-9]+$"
                      helperText="Enter a valid regular expression"
                    />
                  )}

                  {rule.type === 'dependent_value' && (
                    <>
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Depends on Field</InputLabel>
                        <Select
                          value={rule.compareField || ''}
                          onChange={(e) => {
                            const newRules = [...(editField.validation?.custom?.rules || [])];
                            newRules[index] = { ...rule, compareField: e.target.value };
                            setEditField(prev => prev ? {
                              ...prev,
                              validation: {
                                ...prev.validation,
                                custom: {
                                  ...prev.validation?.custom!,
                                  rules: newRules
                                }
                              }
                            } : null);
                          }}
                        >
                          {allFields
                            .filter(f => f.id !== editField.id)
                            .map(field => (
                              <MenuItem key={field.id} value={field.id}>
                                {field.label}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>

                      <TextField
                        fullWidth
                        label="When field has value"
                        value={rule.dependentValue || ''}
                        onChange={(e) => {
                          const newRules = [...(editField.validation?.custom?.rules || [])];
                          newRules[index] = { ...rule, dependentValue: e.target.value };
                          setEditField(prev => prev ? {
                            ...prev,
                            validation: {
                              ...prev.validation,
                              custom: {
                                ...prev.validation?.custom!,
                                rules: newRules
                              }
                            }
                          } : null);
                        }}
                        sx={{ mb: 2 }}
                        placeholder="Specific value that triggers validation"
                      />

                      <TextField
                        fullWidth
                        label="Required Value"
                        value={rule.value || ''}
                        onChange={(e) => {
                          const newRules = [...(editField.validation?.custom?.rules || [])];
                          newRules[index] = { ...rule, value: e.target.value };
                          setEditField(prev => prev ? {
                            ...prev,
                            validation: {
                              ...prev.validation,
                              custom: {
                                ...prev.validation?.custom!,
                                rules: newRules
                              }
                            }
                          } : null);
                        }}
                        sx={{ mb: 2 }}
                        placeholder="Value this field must have"
                      />
                    </>
                  )}

                  <TextField
                    fullWidth
                    label="Error Message"
                    value={rule.message}
                    onChange={(e) => {
                      const newRules = [...(editField.validation?.custom?.rules || [])];
                      newRules[index] = { ...rule, message: e.target.value };
                      setEditField(prev => prev ? {
                        ...prev,
                        validation: {
                          ...prev.validation,
                          custom: {
                            ...prev.validation?.custom!,
                            rules: newRules
                          }
                        }
                      } : null);
                    }}
                    placeholder="Custom error message to show when validation fails"
                    helperText="Message shown to user when this validation rule fails"
                  />
                </Paper>
              ))}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  const newRule = {
                    id: Date.now().toString(),
                    type: 'compare_dates' as const,
                    message: 'Validation failed'
                  };
                  setEditField(prev => prev ? {
                    ...prev,
                    validation: {
                      ...prev.validation,
                      custom: {
                        enabled: true,
                        rules: [...(prev.validation?.custom?.rules || []), newRule]
                      }
                    }
                  } : null);
                }}
                sx={{ mt: 2 }}
              >
                Add Validation Rule
              </Button>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Examples:</strong>
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  • <strong>Date Comparison:</strong> "End date must be greater than start date"
                </Typography>
                <Typography variant="caption" display="block">
                  • <strong>Number Comparison:</strong> "Maximum age cannot be less than minimum age"
                </Typography>
                <Typography variant="caption" display="block">
                  • <strong>Dependent Value:</strong> "If 'Other' is selected, please specify details"
                </Typography>
              </Alert>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          {isEdit ? 'Update' : 'Add'} Field
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormBuilder;
